import { NestedListDetailsProp } from '../../../components/wizardLayout/wizardNestedListContent/types';
import { FieldGroupModel, FieldModel } from '../../../components/form/pageForm/types';
import { yesNoRadioButtonProps } from '../../CommonComponentProps';
import { ProjectItemModel } from '../../../api/models';
import { AFItaxIncentiveInfluenceTypeEnumDesc, AFItaxIncentiveInfluenceType } from '../../../api/models/Enums';
import { SelectInputOptions } from '../../../components/input/selectInput/types';
import { forEach, keys } from 'lodash';

const afiAppGuidanceText: FieldModel<ProjectItemModel> = {
    component: 'globalContent',
    label: '',
    name: 'afiAppGuidanceText',
    contentKey: ['AFIAppGuidanceText'],
};

export const getTaxIncentiveInfluenceOptions = () => {
    const options: SelectInputOptions[] = [{ value: ``, label: `` }];
    forEach(keys(AFItaxIncentiveInfluenceTypeEnumDesc), key => {
        options.push({ label: AFItaxIncentiveInfluenceTypeEnumDesc[key as AFItaxIncentiveInfluenceType], value: key });
    });
    return options;
};

// Q. 54
const taxIncentiveInfluence: FieldModel<ProjectItemModel> = {
    component: 'selectInput',
    label: 'How much did the R&D Tax Incentive influence whether you went ahead with this R&D project?',
    maxWidth: '15rem',
    name: 'taxIncentiveInfluence',
    componentProps: {
        name: 'taxIncentiveInfluence',
        options: getTaxIncentiveInfluenceOptions(),
        radioOptions: [
            {
                id: 'taxIncentiveInfluence-Significantly',
                label: AFItaxIncentiveInfluenceTypeEnumDesc['Significantly'],
                value: 'Significantly',
                tooltip: AFItaxIncentiveInfluenceTypeEnumDesc['Significantly'],
                maxWidth: '812px',
            },
            {
                id: 'taxIncentiveInfluence-SomewhatSignificantly',
                label: AFItaxIncentiveInfluenceTypeEnumDesc['SomewhatSignificantly'],
                value: 'SomewhatSignificantly',
                tooltip: AFItaxIncentiveInfluenceTypeEnumDesc['SomewhatSignificantly'],
                maxWidth: '812px',
            },
            {
                id: 'taxIncentiveInfluence-NotAtAll',
                label: AFItaxIncentiveInfluenceTypeEnumDesc['NotAtAll'],
                value: 'NotAtAll',
                tooltip: AFItaxIncentiveInfluenceTypeEnumDesc['NotAtAll'],
                maxWidth: '812px',
            },
        ],
    },
    contentKey: ['AFITtaxIncentiveInfluence'],
};

// Q. 55
const name: FieldModel<ProjectItemModel> = {
    component: 'textInput',
    label: 'Name for this project',
    name: 'name',
    contentKey: ['AFIProjectName'],
};

// Q. 56
const referenceDescription: FieldModel<ProjectItemModel> = {
    component: 'textInput',
    label: 'Project reference description (optional)',
    name: 'referenceDescription',
    contentKey: ['ProjectReferenceDescription'],
};

// Q. 57
const advancedOrOverseasFinding: FieldModel<ProjectItemModel> = yesNoRadioButtonProps(
    'advancedOrOverseasFinding',
    'Does this project include activities covered by advance or overseas findings?',
    ['ProjectAdvancedOrOverseasFindings']);

// Q. 58
const findingReferenceNumber: FieldModel<ProjectItemModel> = {
    component: 'textInput',
    label: 'What are the certificate reference numbers and dates for the findings?',
    name: 'findingReferenceNumber',
};

// Q. 59
const duration: FieldModel<ProjectItemModel> = {
    component: 'monthYearRangeInput',
    label: 'What is the expected duration of this project?',
    name: 'duration',
    contentKey: ['AFIMonthYearDuration'],
};

// Q. 60 Todo - Yet to implement BR-424, might be OOS
const moneySpent: FieldModel<ProjectItemModel> = {
    component: 'currencyInput',
    label: 'What is the total estimated expenditure for the project?',
    contentKey: ['ProjectExpectedSpend'],
    name: 'moneySpent',
    maxWidth: '352px',
    // Allowing 15 digits plus 4 comma separators as max input length
    maxLength: 19,
};

// Q. 61
const objectives: FieldModel<ProjectItemModel> = {
    component: 'textAreaInput',
    label: 'What are the objectives of this project?',
    contentKey: ['ProjectObjectives'],
    name: 'objectives',
    componentProps: {
        maxCharacters: 4000,
    },
};

// Q. 62
const documentsKept: FieldModel<ProjectItemModel> = {
    component: 'textAreaInput',
    label: 'Describe what documents you have kept, or intend to keep, in relation to the activities in your project.',
    contentKey: ['AFIDocumentsKept'],
    name: 'documentsKept',
    componentProps: {
        maxCharacters: 4000,
    },
};

const plantAddressHeading: FieldModel<ProjectItemModel> = {
    component: 'heading',
    label: 'Plant and facilities address',
    name: 'headerPlantAddress',
    componentProps: {
        tag: 'h2',
        className: 'rdtiH2',
    },
};

// Q. 63
const isPlantAddressSameAsMainBusinessAddress = yesNoRadioButtonProps('isPlantAddressSameAsMainBusinessAddress',
    'Is the location of majority of R&D activities the same as the Main business address provided?', []);

// Q. 64
const showChildErrorsForPlantAddressLookup = (values: ProjectItemModel) => {
    if (values && values.plantAddressLookup && values.plantAddressLookup.isAddressManual) {
        return values.plantAddressLookup.isAddressManual === 'Yes';
    }

    return false;
};

const plantAddressLookup: FieldModel<ProjectItemModel> = {
    component: 'addressLookup',
    label: 'Address lookup',
    name: 'plantAddressLookup',
    maxWidth: '33.5rem',
    contentKey: ['PlantBusinessAddress'],
    componentProps: {
        noMatchText: 'No matches found. Enter the address manually.',
        showChildErrors: showChildErrorsForPlantAddressLookup,
        showAllValidationErrors: true,
        isManualEntryOnly: false,
        fields: {
            addressLine1: {
                component: 'textInput',
                label: 'Address line 1',
                name: 'plantAddressLookup.addressLine1',
                maxWidth: '33.5rem',
            },
            addressLine2: {
                component: 'textInput',
                label: 'Address line 2',
                name: 'plantAddressLookup.addressLine2',
                maxWidth: '33.5rem',
            },
            localityName: {
                component: 'textInput',
                label: 'Suburb',
                name: 'plantAddressLookup.localityName',
                maxWidth: '16.25rem',
            },
            state: {
                component: 'selectInput',
                label: 'State',
                name: 'plantAddressLookup.state',
                maxWidth: '9rem',
                parented: false,
            },
            postcode: {
                component: 'textInput',
                label: 'Postcode',
                name: 'plantAddressLookup.postcode',
                maxWidth: '9rem',
            },
        },
    },
};

// Q. 65
const plantAndFacilitiesDescription: FieldModel<ProjectItemModel> = {
    component: 'textAreaInput',
    label: 'Briefly describe the plant and facilities to be allocated to the project (specialist equipment, facilities etc).',
    contentKey: ['AFIPlantAndFacilitiesDescription'],
    name: 'plantAndFacilitiesDescription',
    componentProps: {
        maxCharacters: 4000,
    },
};

// Q. 66
const feedstockInputsSpent: FieldModel<ProjectItemModel> = {
    component: 'currencyInput',
    label: 'For the selected income period, how much was spent on feedstock inputs?',
    contentKey: ['AFIProjectFeedStockSpend'],
    name: 'feedstockInputsSpent',
    maxWidth: '352px',
    // Allowing 15 digits plus 4 comma separators as max input length
    maxLength: 19,
};

// Q. 67.1
const anzsrcDivision: FieldModel<ProjectItemModel> = {
    component: 'selectInput',
    label: 'ANZSRC Division',
    name: 'anzsrcDivision',
    maxWidth: '812px',
    contentKey: ['ProjectFieldOfResearchANZSRC'],
    componentProps: {
        id: 'anzsrcDivision',
        toggleName: 'Please choose...',
        parented: true,
    },
};

// Q. 67.2
const anzsrcGroup: FieldModel<ProjectItemModel> = {
    component: 'selectInput',
    label: 'ANZSRC Group',
    name: 'anzsrcGroup',
    maxWidth: '812px',
    contentKey: ['ProjectFieldOfResearchANZSRCClassification'],
    componentProps: {
        id: 'anzsrcGroup',
        toggleName: 'Please choose...',
        parented: true,
    },
};

// Q. 67
const organisationAnzsicGroup: FieldGroupModel<ProjectItemModel> = {
    label: 'Which field of research best describes the majority of R&D activities in this project?',
    title: 'Which field of research best describes the majority of R&D activities in this project?',
    name: 'organisationAnzsicGroup',
    displayInRow: false,
    showOneErrorFieldOnly: false,
    groupFields:
    {
        anzsrcDivision,
        anzsrcGroup,
    },
};

// Q. 68
const explainWhyCompanyIsBeneficiary: FieldModel<ProjectItemModel> = {
    component: 'textAreaInput',
    label: 'To establish whether the Company will be a beneficiary of the proposed R&D activities, please describe whether the Company will:',
    contentKey: ['AFIOverallProjectOverview'],
    name: 'explainWhyCompanyIsBeneficiary',
    componentProps: {
        maxCharacters: 4000,
    },
};

const projects: NestedListDetailsProp<ProjectItemModel> = {
    apiEndpoint: '/projects',
    name: 'projects',
    title: 'Project',
    deleteButtonTitle: 'Delete project',
    deleteDialogBodyText: 'Are you sure you want to delete this project?<br>Deleting the project will remove it from any linked core activities, which will need to be linked to another project or deleted.',
    fields: {
        afiAppGuidanceText,
        taxIncentiveInfluence,
        name,
        referenceDescription,
        advancedOrOverseasFinding,
        findingReferenceNumber,
        duration,
        moneySpent,
        objectives,
        documentsKept,
        plantAddressHeading,
        isPlantAddressSameAsMainBusinessAddress,
        plantAddressLookup,
        plantAndFacilitiesDescription,
        feedstockInputsSpent,
        organisationAnzsicGroup,
        explainWhyCompanyIsBeneficiary,
    },
    initialPageValues: {
        taxIncentiveInfluence: undefined,
        name: '',
        referenceDescription: '',
        advancedOrOverseasFinding: undefined,
        findingReferenceNumber: undefined,
        duration: {
            endMonth: undefined,
            endYear: undefined,
            startMonth: undefined,
            startYear: undefined,
        },
        moneySpent: '' as any,
        objectives: undefined,
        documentsKept: '',
        isPlantAddressSameAsMainBusinessAddress: undefined,
        plantAndFacilitiesDescription: '',
        feedstockInputsSpent: '' as any,
        anzsrcDivision: undefined,
        anzsrcGroup: undefined,
        applicationActivityType: [],
        referenceNumber: undefined,
        explainWhyCompanyIsBeneficiary: '',
        plantAddressLookup: {
            isAddressManual: 'No',
            addressLine1: undefined,
            addressLine2: undefined,
            localityName: undefined,
            postcode: undefined,
            state: undefined,
        },
    },
};

export default projects;
